import { Link } from "react-router-dom";
import Reveal from "@/components/feature/Reveal";
import SlimZeroLpLink from "@/components/feature/SlimZeroLpLink";
import { ReserveButton } from "@/components/layout/ReserveButton";
import { LineButton } from "@/components/layout/LineButton";
import { clinic } from "@/mocks/clinic";
import { FeatureList, TreatmentIndexNav, TreatmentSection } from "@/pages/treatment/components/TreatmentSections";
import { DowntimeTable, FlowSteps, MethodTable } from "@/pages/treatment/components/TreatmentProcess";
import { TreatmentFaq } from "@/pages/treatment/components/TreatmentFaqCta";
import TreatmentRelatedLinks from "@/pages/treatment/components/TreatmentRelatedLinks";
import SlimZeroPriceTable from "@/pages/treatment/components/SlimZeroPriceTable";
import {
  slimFlowSteps,
  slimSpecItems,
  slimTreatmentAreas,
  slimZeroTreatments,
  type SlimZeroDetailPage,
} from "@/mocks/slimZeroDetails";
import SlimZeroTreatmentHero from "./SlimZeroTreatmentHero";
import { isPendingValue } from "@/utils/isPendingContent";

/* NOTE: 効果・数値（周波数／温度／照射条件／筋肉増強率など）・機器スペック・作用機序・確定していない料金は
   一切掲載しない。未確認の医療情報は推測して作成しない。構成は /slim/injection と同一。 */

export default function SlimZeroTreatmentDetail({ data }: { data: SlimZeroDetailPage }) {
  const prefix = `sz-${data.slug}`;
  const selfHref = `/slim/${data.slug}`;

  // 施術時間・麻酔・ダウンタイム等がすべて未確定の場合はセクションごと非表示にする
  const downtimeRows = slimSpecItems.filter((item) => !isPendingValue(item.value));

  const indexItems = [
    { label: "この施術について", target: `${prefix}-about` },
    { label: "このようなお悩みの方へ", target: `${prefix}-concerns` },
    { label: "Medical Slim ZEROでの位置づけ", target: `${prefix}-position` },
    { label: "気になる部位", target: `${prefix}-areas` },
    { label: "特徴", target: `${prefix}-features` },
    { label: "施術情報", target: `${prefix}-method` },
    { label: "施術の流れ", target: `${prefix}-flow` },
    ...(downtimeRows.length > 0 ? [{ label: "ダウンタイム", target: `${prefix}-downtime` }] : []),
    { label: "リスク・注意事項", target: `${prefix}-risk` },
    { label: "料金", target: `${prefix}-price` },
    { label: "Q&A", target: `${prefix}-faq` },
  ];

  const otherNamesText = slimZeroTreatments
    .filter((treatment) => treatment.href !== selfHref)
    .map((treatment) => treatment.name)
    .join("、");

  const faqItems = [
    {
      q: "どの部位に施術できますか？",
      a: "施術可能な部位は身体の状態や治療内容によって異なります。気になる部位についてカウンセリング・診察時にご相談ください。",
    },
    {
      q: "何回くらい受ける必要がありますか？",
      a: "必要な治療回数は施術部位や身体の状態、ご希望などによって異なります。診察時に治療プランをご案内します。",
    },
    {
      q: "他のMedical Slim ZEROの治療と組み合わせられますか？",
      a: `${otherNamesText}など、他の医療痩身治療についてもご相談いただけます。治療内容は診察のうえご案内します。`,
    },
    {
      q: "ダイエット中でも相談できますか？",
      a: "体重管理や生活習慣についても確認しながら、気になる部位に対する医療痩身の選択肢をご案内します。",
    },
    {
      q: "当日に施術できますか？",
      a: "診察結果や予約状況などにより異なります。詳しくはご予約時にお問い合わせください。",
    },
  ];

  const relatedItems = [
    { label: "Medical Slim ZEROへ戻る", to: "/treatment/slim-zero" },
    ...slimZeroTreatments
      .filter((treatment) => treatment.href !== selfHref)
      .map((treatment) => ({ label: `${treatment.name}を見る`, to: treatment.href })),
  ];

  return (
    <>
      <SlimZeroTreatmentHero data={data} />
      <TreatmentIndexNav items={indexItems} />

      {/* 01 ABOUT */}
      <TreatmentSection id={`${prefix}-about`} en="01 / ABOUT TREATMENT" jp={`${data.jp}とは`} tone="plain">
        <Reveal>
          <div className="max-w-3xl space-y-6 text-[14px] leading-[2.3] text-foreground-700 md:text-[15px]">
            {data.about.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </div>
        </Reveal>
      </TreatmentSection>

      {/* 02 RECOMMENDED FOR */}
      <TreatmentSection
        id={`${prefix}-concerns`}
        en="02 / RECOMMENDED FOR"
        jp="このようなお悩みの方へ"
        tone="tint"
      >
        <Reveal>
          <p className="max-w-2xl text-[13px] leading-[2.1] text-foreground-700 md:text-sm">
            このようなお悩みについてご相談いただけます。
          </p>
          <ul className="mt-8 grid grid-cols-1 gap-px border border-foreground-950/12 bg-foreground-950/12 sm:grid-cols-2">
            {data.concerns.map((concern, index) => (
              <li key={concern} className="flex items-start gap-4 bg-background-50 px-6 py-6">
                <span className="pt-0.5 font-label text-[11px] tracking-[0.2em] text-secondary-500">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <span className="font-heading text-[15px] leading-[1.8] text-foreground-900 md:text-base">
                  {concern}
                </span>
              </li>
            ))}
          </ul>
        </Reveal>
      </TreatmentSection>

      {/* 03 MEDICAL SLIM ZERO */}
      <TreatmentSection
        id={`${prefix}-position`}
        en="03 / MEDICAL SLIM ZERO"
        jp="一つの治療だけに頼らない医療痩身"
        tone="plain"
      >
        <Reveal>
          <div className="max-w-3xl space-y-6 text-[14px] leading-[2.3] text-foreground-700 md:text-[15px]">
            <p>
              Medical Slim ZEROでは、すべての方に同じ治療を行うのではなく、体型・部位・お悩み・ご希望を確認しながら治療方法を検討します。
            </p>
            <p>以下の治療について、単独または組み合わせを含めてご相談いただけます。</p>
            <ul className="space-y-2.5">
              {slimZeroTreatments.map((item) => (
                <li key={item.href} className="flex items-center gap-3 text-foreground-800">
                  <span className="h-px w-5 bg-accent-500" />
                  {item.name}
                </li>
              ))}
            </ul>
            <p>気になる部位や目的に応じて、ご希望に沿った治療をご案内します。</p>
          </div>
        </Reveal>

        <div className="mt-10 grid grid-cols-1 gap-px border border-foreground-950/12 bg-foreground-950/12 sm:grid-cols-2 md:mt-12">
          {slimZeroTreatments.map((treatment, index) => (
            <Link
              key={treatment.href}
              to={treatment.href}
              className="group flex items-center justify-between gap-4 bg-background-50 px-7 py-7 transition-colors hover:bg-secondary-100/50"
            >
              <span className="flex items-baseline gap-4">
                <span className="font-label text-[12px] tracking-[0.25em] text-secondary-600">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <span className="font-heading text-[16px] text-foreground-950 md:text-lg">{treatment.name}</span>
              </span>
              <i
                className="ri-arrow-right-line text-lg text-foreground-400 transition-all group-hover:translate-x-1 group-hover:text-accent-700"
                aria-hidden
              />
            </Link>
          ))}
        </div>
      </TreatmentSection>

      {/* 04 TREATMENT AREAS */}
      <TreatmentSection id={`${prefix}-areas`} en="04 / TREATMENT AREAS" jp="気になる部位を相談できます" tone="tint">
        <Reveal>
          <ul className="grid grid-cols-1 gap-px border border-foreground-950/12 bg-foreground-950/12 sm:grid-cols-2 lg:grid-cols-3">
            {slimTreatmentAreas.map((area, index) => (
              <li key={area} className="flex items-start gap-4 bg-background-50 px-6 py-7">
                <span className="pt-0.5 font-label text-[11px] tracking-[0.2em] text-secondary-500">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <span className="font-heading text-[15px] leading-[1.8] text-foreground-900 md:text-base">
                  {area}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-8 max-w-3xl border-l-2 border-accent-500 pl-4 text-[12px] leading-[2] text-foreground-600">
            施術可能な部位は治療方法や身体の状態によって異なります。詳しくは診察時にご相談ください。
          </p>
        </Reveal>
      </TreatmentSection>

      {/* 05 FEATURES */}
      <TreatmentSection
        id={`${prefix}-features`}
        en="05 / FEATURES"
        jp={`${data.jp}の特徴`}
        tone="plain"
      >
        <FeatureList items={data.features} />
      </TreatmentSection>

      {/* 06 METHOD */}
      <TreatmentSection id={`${prefix}-method`} en="06 / METHOD" jp="施術情報" tone="tint">
        <Reveal>
          <MethodTable items={data.method} />
        </Reveal>
      </TreatmentSection>

      {/* 07 FLOW */}
      <TreatmentSection id={`${prefix}-flow`} en="07 / TREATMENT FLOW" jp="施術の流れ" tone="plain">
        <FlowSteps items={slimFlowSteps} />
      </TreatmentSection>

      {/* 08 DOWNTIME（未確定項目は非表示。全項目が未確定の場合はセクションごと非表示） */}
      {downtimeRows.length > 0 ? (
        <TreatmentSection id={`${prefix}-downtime`} en="08 / DOWNTIME" jp="施術時間・ダウンタイム" tone="tint">
          <DowntimeTable items={downtimeRows} />
        </TreatmentSection>
      ) : null}

      {/* 09 RISK / PRECAUTIONS */}
      <TreatmentSection
        id={`${prefix}-risk`}
        en="09 / RISK / PRECAUTIONS"
        jp="リスク・注意事項"
        tone="plain"
      >
        <Reveal>
          <div className="max-w-3xl border-l-2 border-accent-500 pl-5 md:pl-6">
            <div className="space-y-6 text-[14px] leading-[2.3] text-foreground-700 md:text-[15px]">
              {data.risk.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>
          </div>
        </Reveal>
      </TreatmentSection>

      {/* 10 PRICE */}
      <TreatmentSection id={`${prefix}-price`} en="10 / PRICE" jp="料金" tone="tint">
        <div className="space-y-10">
          <SlimZeroPriceTable onlySlug={data.slug} />
          <SlimZeroLpLink
            note="プラン・詳細はMedical Slim ZERO専用ページでご確認いただけます。"
            label="Medical Slim ZEROのプラン・詳細を見る"
          />
        </div>
      </TreatmentSection>

      {/* 11 FAQ */}
      <TreatmentSection id={`${prefix}-faq`} en="11 / Q&amp;A" jp="よくあるご質問" tone="plain">
        <div className="max-w-3xl">
          <TreatmentFaq items={faqItems} />
        </div>
      </TreatmentSection>

      {/* RELATED LINKS */}
      <TreatmentRelatedLinks items={relatedItems} />

      {/* FINAL CTA */}
      <section className="relative overflow-hidden bg-primary-700 py-20 md:py-24 lg:py-28">
        <div
          aria-hidden
          className="pointer-events-none absolute -right-20 top-1/2 h-72 w-72 -translate-y-1/2 rounded-full border border-background-50/10"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -right-40 top-1/2 h-[420px] w-[420px] -translate-y-1/2 rounded-full border border-background-50/10"
        />
        <div className="u-container relative">
          <Reveal>
            <div className="mx-auto flex max-w-3xl flex-col items-center text-center">
              <span className="en-eyebrow text-[10px] text-accent-300 md:text-[11px]">CONTACT / RESERVATION</span>
              <h2 className="mt-7 font-heading text-[24px] leading-[1.7] text-background-50 md:text-[34px]">
                どの医療痩身が自分に合っているか
                <br />
                分からない方もご相談ください。
              </h2>
              <p className="mt-7 max-w-xl text-[13px] leading-[2.2] text-background-200 md:text-sm">
                体型や気になる部位、ご希望を伺い、一人ひとりに合わせて治療をご提案します。
              </p>

              <div className="mt-10 flex w-full flex-col items-stretch gap-3 sm:w-auto sm:flex-row sm:items-center">
                <LineButton className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-background-50 px-8 py-3.5 text-[14px] font-medium text-primary-700 transition-colors hover:bg-secondary-100">
                  <i className="ri-line-fill text-base" aria-hidden />
                  {data.ctaLabel}
                </LineButton>
                <ReserveButton className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 px-8 py-3.5 text-[14px] text-background-50 transition-colors hover:bg-background-50/10">
                  <i className="ri-calendar-check-line text-base" aria-hidden />
                  無料カウンセリングを予約
                </ReserveButton>
              </div>

              <p className="mt-8 text-[12px] tracking-wide text-background-200/80">
                {clinic.hours} ／ 渋谷駅近く
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}